import React, {Fragment} from 'react';
import PropTypes from 'prop-types';
import {graphql} from 'react-apollo';
import Helmet from 'react-helmet-async';
import {get, isEmpty} from 'lodash';
import classNames from 'classnames';
import updateADL from '../../utils/adl';
import Experiment from '@homeaway/react-experiment';
import Nps from '@homeaway/react-nps';
import {Unveil} from '@homeaway/react-unveil';
import {ComponentError} from '../ComponentError/ComponentError';
import {ConfirmationQuery} from './ConfirmationQuery';
import {CancelProtection} from '../CancelProtection/CancelProtection';
import {Faqs} from './Faqs/Faqs';
import {NextSteps} from './NextSteps/NextSteps';
import {Receipt} from './Receipt/Receipt';
import {RentalInformation} from './RentalInformation/RentalInformation';
import AppCTABannerContainer from '../AppCTABannerContainer/AppCTABannerContainer';
import AppCTAModalBannerContainer from '../AppCTAModalBannerContainer/AppCTAModalBannerContainer';
import {TrackingTrigger} from '@homeaway/react-event-tracking';
import {Link} from '@homeaway/react-localization-components';
import LoyaltyWrapper from './LoyaltyWrapper/LoyaltyWrapper';
import {
    bookingConfirmationPresented,
    TRIPS,
    BOOKING_CONFIRMATION,
} from '../../utils/edapUtils';
import haWrapper from '../../utils/haWrapper';
import {StayProvider} from '../context/StayContext';
import {SystemProvider} from '../context/SystemContext';
import {createSystemDataObject} from '../../utils/systemData';
import type {System} from '../../utils/systemData';

import './Confirmation.less';
import {
    getItem1,
    getItem2,
    getItem3,
    getItem4,
    getPaymentSplit,
} from '../../utils/analyticsDataMapping';
import translate from '../../utils/translations';
import ErrorMessages from './ErrorMessages/ErrorMessages';
import {extendTcVars} from '../../utils/tcVarsUpdater';

export class Confirmation extends React.Component {
    static propTypes = {
        isVrbexNative: PropTypes.bool,
        reservationId: PropTypes.string,
        systemData: PropTypes.shape({}),
        translations: PropTypes.shape({}),
        unitLink: PropTypes.string,
        bookingFlow: PropTypes.string,
    };

    static defaultProps = {
        isVrbexNative: false,
        reservationId: '',
        translations: {},
        unitLink: '',
    };

    componentDidMount() {
        haWrapper.userTiming.mark('primary.action.rendered');
        updateADL('travelerdash booking confirmation');

        bookingConfirmationPresented({
            action_location: TRIPS,
            reservation_id: this.props.reservationId,
            ...this.props?.data?.bookingConfirmation?.edapAnalyticsPayload,
            item4: getItem4(
                get(this.props, 'data.bookingConfirmation.listing')
            ),
            item3: getItem3(
                get(this.props, 'data.bookingConfirmation.listing')
            ),
            item2: getItem2(
                get(this.props, 'data.bookingConfirmation.listing')
            ),
            item1: getItem1(
                get(this.props, 'data.bookingConfirmation.listing')
            ),
            paymentsplit: getPaymentSplit(
                get(this.props, 'data.ecomPriceDetails'),
                get(
                    this.props,
                    'data.bookingConfirmation.bookingSummary.reservationStatus'
                )
            ),
            booking_flow: get(this.props, 'bookingFlow'),
            lodging_directory_id: get(
                this.props,
                'data.bookingConfirmation.listing.lodgingDirectoryId'
            ),
        });
        window.analyticsdatalayer.integratedPropertyManager = get(
            this.props,
            'data.bookingConfirmation.listing.integratedPropertyManager',
            false
        );
        extendTcVars({
            lodging_directory_id: get(
                this.props,
                'data.bookingConfirmation.listing.lodgingDirectoryId'
            ),
        });
    }

    get guests() {
        const adults = get(
            this.props,
            'data.bookingConfirmation.bookingSummary.numAdults',
            0
        );
        const children = get(
            this.props,
            'data.bookingConfirmation.bookingSummary.numChildren',
            0
        );
        const pets = get(
            this.props,
            'data.bookingConfirmation.bookingSummary.numPets',
            0
        );

        return translate(
            this.props.translations,
            'ui-development/shared',
            'stabGuestSelector',
            'shared.stabGuestSelector.guests.display',
            {ADULTS: adults, CHILDREN: children, PETS: pets}
        );
    }
    render() {
        const {isVrbexNative, translations, reservationId, systemData} =
            this.props;
        const data = get(this.props, 'data', {});
        const {isMobile = false, site} = systemData || {};
        const {humanReadableBrandName = '', humanReadableName = ''} =
            site || {};
        const confirmation = data.bookingConfirmation || {};
        const ecomPriceDetails = data.ecomPriceDetails || {};
        const bookingSummary = confirmation.bookingSummary || {};
        const ctaDetails = confirmation.ctaDetails || {};
        const listing = confirmation.listing || {};
        const vasSummary = confirmation.valueAddedServicesSummary || {};
        const loyaltyEligibleAmounts =
            ecomPriceDetails.loyaltyEligibleAmounts || {};
        const address = get(listing, 'geography.description');
        const listingPhoto = get(
            listing,
            'images[0].uri',
            'http://csvcus.homeaway.com/rsrcs/cdn-logos/2.12.0/bce/brand/misc/property-image-placeholder.svg'
        );
        const paymentConfirmation = confirmation.paymentConfirmation;
        const stayData = {
            stay: {
                booking: {
                    reservationUuid: reservationId,
                },
                unit: {
                    integratedPropertyManager:
                        listing.integratedPropertyManager,
                },
            },
        };
        const systemDataUtils: System = createSystemDataObject({
            isDesktop: systemData.isDesktop,
        });
        const {siteFees, sitePrice, siteTaxes} = loyaltyEligibleAmounts;
        const tripEndDate = bookingSummary.checkoutDate
            ? bookingSummary.checkoutDate
            : bookingSummary.localizedCheckoutDate;
        const tripStartDate = bookingSummary.checkinDate
            ? bookingSummary.checkinDate
            : bookingSummary.localizedCheckinDate;
        const notificationBannersData = {
            isVrbexNative,
            systemData,
            userEmail: bookingSummary.travelerEmail,
            siteFees,
            sitePrice,
            siteTaxes,
            tripEndDate,
            tripStartDate,
            paymentCurrency: ecomPriceDetails?.totals?.[0]?.currencyCode,
        };
        const title = (
            <Helmet>
                <title>
                    {translate(
                        translations,
                        'trav-dash/trav-home',
                        'nav',
                        'th.tabs.confirmation'
                    )}
                </title>
            </Helmet>
        );
        if (data.loading || isEmpty(confirmation)) {
            return (
                <section className="container">
                    {title}
                    <div className="Confirmation">
                        <div className="confirmation-item">
                            <ComponentError
                                loading={data.loading}
                                translations={translations}
                            />
                        </div>
                    </div>
                </section>
            );
        }

        // Full width will be shown on desktop and tablet when there is no widget to be shown on right rail.
        const isFullWidth = !vasSummary.vascartUrl;

        const leftRailClassName = classNames('col-xs-12', {
            'col-md-12 col-sm-12': isFullWidth,
            'col-md-8 col-sm-7 Confirmation__leftRail': !isFullWidth,
        });
        const rightRailClassName = classNames(
            'col-xs-12',
            'col-md-4',
            'col-sm-5',
            {
                Confirmation__rightRail: !isFullWidth,
            }
        );

        return (
            <SystemProvider value={systemDataUtils}>
                <section className="container">
                    {title}
                    <div className="Confirmation row">
                        <div className={leftRailClassName}>
                            <ErrorMessages
                                confirmation={confirmation}
                                translations={translations}
                            />
                            <Link href={ctaDetails.ctaLink}>
                                {(localizedUrl) => (
                                    <Experiment
                                        name={'vrbo_ipm_static_messaging'}
                                    >
                                        {({On, Off}) => {
                                            const commonProps = {
                                                email: bookingSummary.travelerEmail,
                                                hostName: listing.contact?.name,
                                                instantBooking:
                                                    bookingSummary.instantBooking,
                                                isIpm: listing.integratedPropertyManager,
                                                isVrbexNative,
                                                paymentConfirmation,
                                                reservationId:
                                                    bookingSummary.reservationReferenceNumber,
                                                reservationStatus:
                                                    bookingSummary.reservationStatus,
                                                translations,
                                                confirmation,
                                                tripDetailsUrl: localizedUrl,
                                            };
                                            return (
                                                <Fragment>
                                                    <On>
                                                        <NextSteps
                                                            {...commonProps}
                                                            isVrboIpmStaticMessaging
                                                        />
                                                    </On>
                                                    <Off>
                                                        <NextSteps
                                                            {...commonProps}
                                                            isVrboIpmStaticMessaging={
                                                                false
                                                            }
                                                        />
                                                    </Off>
                                                </Fragment>
                                            );
                                        }}
                                    </Experiment>
                                )}
                            </Link>
                            <AppCTABannerContainer
                                {...notificationBannersData}
                            />
                            {data?.ecomPriceDetails?.loyalty && (
                                <LoyaltyWrapper
                                    loyaltyData={data.ecomPriceDetails.loyalty}
                                />
                            )}
                            <AppCTAModalBannerContainer
                                {...notificationBannersData}
                            />
                            {!isVrbexNative && (
                                <>
                                    {!paymentConfirmation && (
                                        <Link href={ctaDetails.ctaLink}>
                                            {(localizedUrl) => (
                                                <RentalInformation
                                                    actionLocation={
                                                        BOOKING_CONFIRMATION
                                                    }
                                                    address={address}
                                                    instantBooking={
                                                        bookingSummary.instantBooking
                                                    }
                                                    listingPhoto={listingPhoto}
                                                    listingId={
                                                        listing.propertyId
                                                    }
                                                    listingUrl={
                                                        listing.detailPageUrl
                                                    }
                                                    localGuests={this.guests}
                                                    localizedArrivalTime={
                                                        bookingSummary.localizedArrivalTime
                                                    }
                                                    localizedCheckinDate={
                                                        bookingSummary.localizedCheckinDate
                                                    }
                                                    localizedCheckoutDate={
                                                        bookingSummary.localizedCheckoutDate
                                                    }
                                                    localizedDepartureTime={
                                                        bookingSummary.localizedDepartureTime
                                                    }
                                                    reservationReferenceNumber={
                                                        bookingSummary.reservationReferenceNumber
                                                    }
                                                    reservationUuid={
                                                        reservationId
                                                    }
                                                    translations={translations}
                                                    tripDetailsUrl={
                                                        localizedUrl
                                                    }
                                                />
                                            )}
                                        </Link>
                                    )}
                                    {!paymentConfirmation && (
                                        <Nps
                                            lingua={{
                                                cancel: translate(
                                                    translations,
                                                    'ui-development/shared',
                                                    'npsWidget',
                                                    'shared.npsWidget.cancel'
                                                ),
                                                charactersRemaining: translate(
                                                    translations,
                                                    'ui-development/shared',
                                                    'npsWidget',
                                                    'shared.npsWidget.charactersRemaining',
                                                    {0: '{0}'}
                                                ),
                                                detractor: translate(
                                                    translations,
                                                    'ui-development/shared',
                                                    'npsWidget',
                                                    'shared.npsWidget.detractor',
                                                    {0: humanReadableName}
                                                ),
                                                likelyText: translate(
                                                    translations,
                                                    'ui-development/shared',
                                                    'npsWidget',
                                                    'shared.npsWidget.likelyText'
                                                ),
                                                notLikelyText: translate(
                                                    translations,
                                                    'ui-development/shared',
                                                    'npsWidget',
                                                    'shared.npsWidget.notLikelyText'
                                                ),
                                                optional: translate(
                                                    translations,
                                                    'ui-development/shared',
                                                    'npsWidget',
                                                    'shared.npsWidget.optional'
                                                ),
                                                promoter: translate(
                                                    translations,
                                                    'ui-development/shared',
                                                    'npsWidget',
                                                    'shared.npsWidget.promoter',
                                                    {0: humanReadableName}
                                                ),
                                                question: translate(
                                                    translations,
                                                    'ui-development/shared',
                                                    'npsWidget',
                                                    'shared.npsWidget.question',
                                                    {0: humanReadableBrandName}
                                                ),
                                                selectDefaultText: translate(
                                                    translations,
                                                    'ui-development/shared',
                                                    'npsWidget',
                                                    'shared.npsWidget.selectDefaultText'
                                                ),
                                                siteName: translate(
                                                    translations,
                                                    'ui-development/shared',
                                                    'npsWidget',
                                                    'shared.npsWidget.siteName'
                                                ),
                                                submit: translate(
                                                    translations,
                                                    'ui-development/shared',
                                                    'npsWidget',
                                                    'shared.npsWidget.submit'
                                                ),
                                                thankYouBody: translate(
                                                    translations,
                                                    'ui-development/shared',
                                                    'npsWidget',
                                                    'shared.npsWidget.thankYouBody',
                                                    {0: humanReadableName}
                                                ),
                                                thankYouHeader: translate(
                                                    translations,
                                                    'ui-development/shared',
                                                    'npsWidget',
                                                    'shared.npsWidget.thankYouHeader',
                                                    {0: humanReadableBrandName}
                                                ),
                                            }}
                                        />
                                    )}

                                    {!isEmpty(data.ecomPriceDetails) && (
                                        <StayProvider value={{...stayData}}>
                                            <Receipt
                                                priceDetails={
                                                    data.ecomPriceDetails
                                                }
                                                translations={translations}
                                            />
                                        </StayProvider>
                                    )}

                                    {confirmation.krazyGlueUrl && (
                                        <Unveil>
                                            <Experiment
                                                name={'HA_STAY_krazy_glue'}
                                                variant={'on'}
                                            >
                                                <TrackingTrigger
                                                    event={'generic.event'}
                                                    fields={{
                                                        eventaction:
                                                            'krazyglue.unveil',
                                                        eventcategory:
                                                            'traveler activity',
                                                    }}
                                                />
                                                <Helmet>
                                                    <script
                                                        src={
                                                            confirmation.krazyGlueUrl
                                                        }
                                                    />
                                                </Helmet>
                                            </Experiment>
                                        </Unveil>
                                    )}
                                    {vasSummary.vascartUrl && (
                                        <div className="confirmation-item hidden-lg hidden-md hidden-sm">
                                            <Link href={vasSummary.vascartUrl}>
                                                {(localizedUrl) => (
                                                    <CancelProtection
                                                        cancelCtaLink={
                                                            localizedUrl
                                                        }
                                                        translations={
                                                            translations
                                                        }
                                                    />
                                                )}
                                            </Link>
                                        </div>
                                    )}
                                    {!paymentConfirmation && (
                                        <Faqs
                                            instantBooking={
                                                bookingSummary.instantBooking
                                            }
                                            translations={translations}
                                            tripDetailsUrl={ctaDetails.ctaLink}
                                            humanReadableBrandName={
                                                humanReadableBrandName
                                            }
                                        />
                                    )}
                                </>
                            )}
                        </div>
                        {!isVrbexNative && !(isMobile || isFullWidth) && (
                            <div className={rightRailClassName}>
                                {vasSummary.vascartUrl && (
                                    <div className="confirmation-item hidden-xs">
                                        <Link href={vasSummary.vascartUrl}>
                                            {(localizedUrl) => (
                                                <CancelProtection
                                                    cancelCtaLink={localizedUrl}
                                                    translations={translations}
                                                />
                                            )}
                                        </Link>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </section>
            </SystemProvider>
        );
    }
}

export default graphql(ConfirmationQuery, {
    options: (ownProps) => ({
        variables: {
            reservationId: ownProps.reservationId,
            unitLink: ownProps.unitLink,
        },
        errorPolicy: 'all',
    }),
})(Confirmation);
